import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

import { AppLog } from "services";
import { StatusMsg } from "utils";
import { Button } from "components/ui/button";

interface DialogProps {
  setIsDialogOpen: (value: boolean) => void;
  videoUrl: string;
  isOpen: boolean;
}

export function DialogViewVideo({ setIsDialogOpen, videoUrl, isOpen }: DialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent isShowBtnXClose={false} className="max-w-full lg:max-w-[700px] h-[calc(100vh-80px)] py-2 px-0 flex flex-col" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="mt-2 text-center">{t("View Video")}</DialogTitle>

          <div
            className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
            onClick={() => setIsDialogOpen(false)}
          >
            <X />
          </div>
        </DialogHeader>

        <div className="flex flex-grow w-full h-full  overflow-auto ">{videoUrl && <VideoPlayer videoUrl={videoUrl} />}</div>
      </DialogContent>
    </Dialog>
  );
}

export default function VideoPlayer({ videoUrl }: { videoUrl: string }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    try {
      const video = videoRef.current;
      if (!video) return;

      if (playing) {
        video.pause();
      } else {
        video.play();
      }
      setPlaying(!playing);
    } catch (error) {
      AppLog.show({
        message: `[togglePlay]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !muted;
    setMuted(!muted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  };

  const formatTime = (sec: number) => {
    const minutes = Math.floor(sec / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setCurrentTime(0);
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => setPlaying(false);

    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  return (
    <div className="w-full h-full space-y-4 relative p-4">
      {videoUrl && (
        <div className="relative w-full h-full max-w-3xl bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            src={videoUrl}
            className="relative z-10 w-full h-full object-contain"
            playsInline
            muted={muted}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            controls={false}
          />

          {!playing && (
            <button onClick={togglePlay} className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 hover:bg-black/40 transition">
              <Play size={48} className="text-white" />
            </button>
          )}

          <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 pt-1 z-30 bg-gradient-to-t from-black/60 to-transparent">
            <div className="flex items-center justify-between space-x-4">
              <div className="flex space-x-2">
                <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={togglePlay}>
                  {playing ? <Pause size={16} /> : <Play size={16} />}
                </Button>

                <Button variant="secondary" size="icon" className="pointer-events-auto w-8 h-8 rounded-full" onClick={toggleMute}>
                  {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </Button>
              </div>

              <div className="text-xs text-white">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            <input
              type="range"
              min={0}
              max={duration}
              step={0.01}
              value={currentTime}
              onChange={handleSeek}
              style={{
                background: `linear-gradient(to right, #3b82f6 ${(currentTime / duration) * 100}%, #d1d5db ${(currentTime / duration) * 100}%)`,
              }}
              className="w-full h-[4px] mt-2 appearance-none rounded-full
                      [&::-webkit-slider-thumb]:appearance-none
                      [&::-webkit-slider-thumb]:h-3
                      [&::-webkit-slider-thumb]:w-3
                      [&::-webkit-slider-thumb]:rounded-full
                      [&::-webkit-slider-thumb]:bg-blue-500
                      [&::-webkit-slider-thumb]:cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
}

